import L from 'leaflet';
import { growingStatusColor, GROWING_STATUS_ORDER } from './growingStatuses';

/**
 * Growing-layer markers: one teardrop pin per region, and a cluster pin for a
 * group of them.
 *
 * The silhouette is the design's own exported vector, not an approximation —
 * 42.8 x 57.975, a circular head of radius 21.4 centred at (21.4, 21.4)
 * tapering to a point. Earlier revisions of this file fitted a curve by eye
 * against a screenshot; the real path is here, so nothing needs fitting.
 *
 * Why this doesn't reuse `createLeafletPinIcon`: that icon draws into a 24x24
 * viewBox the artwork only fills 14x19 of, so its `sizePx` is the box, not the
 * pin. Here `sizePx` means the pin's own width. Weather events still use the
 * shared icon; only growing regions live here.
 */

/** The design's pin, in its own units. */
const PIN_W = 42.8;
const PIN_H = 57.975;
/** Head centre and radius — the ring and the count both hang off these. */
const HEAD_X = 21.4;
const HEAD_Y = 21.4;
const HEAD_R = 21.4;

const PIN_PATH = 'M21.4 0C9.6 0 0 9.6 0 21.4C0 36.6 18.2 55.5 19.9 57.3C20.7 58.2 22.1 58.2 22.9 57.3C24.6 55.5 42.8 36.6 42.8 21.4C42.8 9.6 33.2 0 21.4 0Z';

/** Padding around the artwork so the white edge and shadow are not clipped. */
const PAD = 2.5;
const VB = `${-PAD} ${-PAD} ${PIN_W + PAD * 2} ${PIN_H + PAD * 2}`;
const VB_W = PIN_W + PAD * 2;
const VB_H = PIN_H + PAD * 2;

// Pin sizes are the same on every layer, and matched to the pin production
// already draws, NOT to a raw pixel number.
//
// This is the trap described above, from the other side: `createLeafletPinIcon`
// at sizePx 28 fills only 14x19 of its 24x24 box, so its pin is really
// 28 * 14/24 = 16.3px wide. Setting 28 here produced a pin 1.7x wider than the
// one beside it on the same map, which is why region pins looked oversized
// against the weather tiles.
//
// 16 reproduces production's width; the two silhouettes have practically the
// same aspect ratio (14:19 = 0.737 against 42.8:57.975 = 0.738), so matching
// the width matches the height too — 16 x 21.7 here against 16.3 x 22.2 there.
/** The region pin's outer width. Matches production's effective pin width. */
export const GROWING_PIN_WIDTH = 16;
/**
 * Cluster width — the design's own size, not a step up from the region pin.
 *
 * The frame in Figma is 48x60 and the artwork inside it measures 42.80x57.98,
 * which is this path's native size to two decimals: the design draws the
 * cluster 1:1. `sizePx` is the pin's own width, so 43 renders a 48.0x63.3
 * element — the 48 the design asks for, the extra height being the padding
 * that keeps the white edge and shadow from clipping.
 *
 * It is deliberately far larger than the 16px region pin. A cluster stands for
 * many regions, and at near-equal sizes the two were indistinguishable.
 */
export const GROWING_CLUSTER_WIDTH = 43;

/**
 * The pin's "paper" — the colour its white parts take.
 *
 * On light tiles that is white: the edge around a region pin, the dot in its
 * head, and the whole body of a cluster. On dark tiles white glares against
 * the map, so it becomes #3D403E and the cluster's count flips to white to
 * stay legible on it. One pair of constants rather than per-call-site
 * conditionals, so a pin and a cluster can never end up on different papers.
 */
const PAPER = { light: '#fff', dark: '#3D403E' };
const INK = { light: '#282A29', dark: '#fff' };
const paperOf = (isDark) => (isDark ? PAPER.dark : PAPER.light);
const inkOf = (isDark) => (isDark ? INK.dark : INK.light);

/** Edge so a pin stays legible over any tile colour. */
const EDGE_W = 2.6;
/** The dot inside a single region's pin. */
const DOT_R = 7.5;

// Ring geometry, measured off the design render against its head radius:
// inner 0.56R, outer 0.81R. Expressed here as the stroked circle that sits
// between them, which is how the arcs are actually drawn.
const RING_R = HEAD_R * 0.685;
const RING_W = HEAD_R * 0.25;
/** Degrees of clear space between adjacent arcs, as in the design. */
const ARC_GAP_DEG = 3;
/** Status order around the ring, clockwise from 12 o'clock — the registry's
 *  display order, so the ring, the legend and the overview all agree. */
const RING_ORDER = GROWING_STATUS_ORDER;

function escapeHtmlAttr(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

/** Rendered size and tip anchor for a pin whose own width is `sizePx`. */
function frame(sizePx) {
  const pinW = Math.max(12, sizePx);
  const width = (pinW * VB_W) / PIN_W;
  const height = (width * VB_H) / VB_W;
  return {
    width,
    height,
    // The pin stands on its tip, not the middle of its box.
    anchor: [
      (width * (HEAD_X + PAD)) / VB_W,
      (height * (PIN_H + PAD)) / VB_H,
    ],
  };
}

function divIcon({ svg, width, height, anchor, muted, cluster = false }) {
  return L.divIcon({
    className: `leaflet-pin-icon${muted ? ' leaflet-pin-icon--muted' : ''}`
      + `${cluster ? ' leaflet-pin-icon--cluster' : ''}`,
    html: svg,
    iconSize: [width, height],
    iconAnchor: anchor,
    popupAnchor: [0, -anchor[1] + 4],
  });
}

/**
 * One region's pin, filled with its production status colour.
 *
 * @param {object}  opts
 * @param {string}  opts.status  A `growingStatuses` id; an unknown one falls
 *                               back to grey so a pin is never colourless.
 * @param {number}  opts.sizePx  The pin's outer width. Height follows.
 * @param {boolean} opts.muted   Dimmed, for the All layer where regions share
 *                               the map with shipments and weather.
 * @returns {L.DivIcon}
 */
export function createGrowingPinIcon({
  status, sizePx = GROWING_PIN_WIDTH, muted = false, isDark = false,
} = {}) {
  const fill = growingStatusColor(status);
  const paper = paperOf(isDark);
  const { width, height, anchor } = frame(sizePx);

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"
         viewBox="${VB}" aria-hidden="true" focusable="false"
         style="display:block;--pin-glow:${escapeHtmlAttr(fill)};">
      <path d="${PIN_PATH}" fill="${escapeHtmlAttr(fill)}" stroke="${paper}" stroke-width="${EDGE_W}" stroke-linejoin="round"/>
      <circle cx="${HEAD_X}" cy="${HEAD_Y}" r="${DOT_R}" fill="${paper}"/>
    </svg>
  `;

  return divIcon({ svg, width, height, anchor, muted });
}

/** A point on the ring. 0deg is 12 o'clock and angles run clockwise. */
function ringPoint(deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [
    (HEAD_X + Math.cos(rad) * RING_R).toFixed(3),
    (HEAD_Y + Math.sin(rad) * RING_R).toFixed(3),
  ];
}

/**
 * The ring arcs, one per status present, each sized to its share of the group.
 *
 * A single status fills the ring, which cannot be drawn as one arc — an arc
 * whose ends coincide renders as nothing — so that case becomes a circle.
 */
function ringArcs(counts, total) {
  const present = RING_ORDER.filter((s) => (counts[s] || 0) > 0);
  if (present.length === 0) return '';
  if (present.length === 1) {
    return `<circle cx="${HEAD_X}" cy="${HEAD_Y}" r="${RING_R}" fill="none"
      stroke="${escapeHtmlAttr(growingStatusColor(present[0]))}"
      stroke-width="${RING_W}"/>`;
  }
  let cursor = 0;
  return present.map((status) => {
    const share = (counts[status] / total) * 360;
    const start = cursor + ARC_GAP_DEG / 2;
    const end = cursor + share - ARC_GAP_DEG / 2;
    cursor += share;
    const [x0, y0] = ringPoint(start);
    const [x1, y1] = ringPoint(end);
    const large = end - start > 180 ? 1 : 0;
    return `<path d="M${x0} ${y0} A${RING_R} ${RING_R} 0 ${large} 1 ${x1} ${y1}" fill="none"
      stroke="${escapeHtmlAttr(growingStatusColor(status))}"
      stroke-width="${RING_W}" stroke-linecap="butt"/>`;
  }).join('');
}

/**
 * The pin a group of overlapping regions collapses into.
 *
 * Unlike a region pin this one is white, and carries the group's makeup as a
 * ring of arcs around the count — one arc per status, each as long as that
 * status's share. Painting the whole pin in a single "dominant" colour, as an
 * earlier revision did, threw that makeup away: a group of nine producing and
 * one failing region looked exactly like ten healthy ones.
 *
 * @param {object} opts
 * @param {Record<string, number>} opts.counts  Regions per status.
 * @param {number} opts.count   Total regions; the number in the middle.
 * @param {number} opts.sizePx  Outer width.
 * @param {boolean} opts.muted  Dimmed, for the All layer.
 * @returns {L.DivIcon}
 */
export function createGrowingClusterIcon({
  counts = {},
  count,
  sizePx = GROWING_CLUSTER_WIDTH,
  muted = false,
  isDark = false,
} = {}) {
  const paper = paperOf(isDark);
  const ink = inkOf(isDark);
  const { width, height, anchor } = frame(sizePx);
  const total = count || Object.values(counts).reduce((a, b) => a + b, 0);
  const label = total > 999 ? '999+' : String(total);
  // The ring's inner edge is ~12 units from centre, so a label has ~24 to live
  // in. Three and four glyphs give back what two fit comfortably.
  const fontSize = label.length >= 4 ? 8 : label.length === 3 ? 10 : 14;

  // A cluster is white, so it has no colour of its own to glow with. It
  // borrows the status it mostly contains, which is also the arc that takes
  // up most of its ring — so the halo agrees with what the pin already shows.
  const dominant = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])[0];
  const dominantColor = dominant ? growingStatusColor(dominant[0]) : '#9CA3AF';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"
         viewBox="${VB}" aria-hidden="true" focusable="false"
         style="display:block;--pin-glow:${escapeHtmlAttr(dominantColor)};">
      <path d="${PIN_PATH}" fill="${paper}"/>
      ${ringArcs(counts, total)}
      <text x="${HEAD_X}" y="${HEAD_Y}" text-anchor="middle" dominant-baseline="central"
            font-family="Inter, system-ui, sans-serif" font-size="${fontSize}"
            font-weight="700" fill="${ink}">${escapeHtmlAttr(label)}</text>
    </svg>
  `;

  return divIcon({ svg, width, height, anchor, muted, cluster: true });
}

export default createGrowingPinIcon;
